import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '../../../../../components/ui/Button';
import { Modal } from '../../../../../components/ui/Modal';
import { setDeliveryItemsCount } from '../../../../../features/deliveryItems/deliveryItemsThunks';

// Same cap as the backend's MAX_DELIVERY_ITEMS.
const MAX_ITEMS = 100;

/**
 * Sets the total number of bultos in one step.
 *
 * The backend adds or removes from the end of the list, so an operator who
 * already filled in the logistics of bulto 1 keeps that work when going from
 * two bultos to three.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number} props.currentCount
 * @param {number|string} props.orderId
 * @param {(message: string) => void} props.onError
 */
const SetItemsCountModal = ({ isOpen, onClose, currentCount, orderId, onError }) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  // The count as last seeded. A newer count replaces the field only while the
  // user has not typed: a late refresh corrects a stale seed, never a number
  // being entered.
  const seeded = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      seeded.current = null;
      return;
    }
    const next = String(currentCount);
    const previous = seeded.current;
    seeded.current = next;
    setValue((current) => (previous === null || current === previous ? next : current));
  }, [isOpen, currentCount]);

  const parsed = parseInt(value, 10);
  const isValid = parsed >= 1 && parsed <= MAX_ITEMS && parsed !== currentCount;

  const handleSave = async () => {
    // Enter in the field lands here too, past the disabled button.
    if (!isValid || saving) return;
    setSaving(true);
    try {
      await dispatch(setDeliveryItemsCount({ orderId, count: parsed })).unwrap();
      onClose();
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Establecer cantidad de bultos" size="sm" testId="order-items-count-modal" busy={saving}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary">
          Total actual: <b className="text-ninja-dark dark:text-white">{currentCount}</b>{' '}
          bulto{currentCount !== 1 ? 's' : ''}. Los bultos extra se agregarán o eliminarán desde
          el final.
        </p>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5">
            Nueva cantidad total
          </label>
          <div className="relative">
            <input
              type="number"
              min="1"
              max={MAX_ITEMS}
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              className="w-full bg-transparent border border-border rounded-xl pl-4 pr-16 py-2.5 text-sm focus:outline-none focus:border-ninja-green transition-colors"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-text-muted">
              bultos
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-6">
        <Button
          variant="subtle"
          pill
          onClick={onClose}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          pill
          onClick={handleSave}
          loading={saving}
          loadingLabel="Estableciendo..."
          disabled={!isValid}
        >
          Establecer
        </Button>
      </div>
    </Modal>
  );
};

export default SetItemsCountModal;
